// `useExtracted()` / `getExtracted()` without compilation to catalog keys.
//
// English is the source locale, so the message written in the call is the English text, and the
// public site ships no catalog (see vite/messageExtraction.ts). The portal can show another locale:
// its translator looks each message up by that English text in the locale's catalog (./catalogs.ts)
// and falls back to the English. use-intl's translator formats a message passed as its (internal)
// fallback argument when the key does not resolve, which is what use-intl's own development-only
// `useExtracted` does; its production build refuses uncompiled calls, so the app provides the same
// translator here for both builds.
import { createTranslator } from "use-intl/core";
import type { _useExtracted } from "use-intl/react";
import type { Catalog } from "./catalogs";
import { type AppLocale, DEFAULT_LOCALE, DEFAULT_TIME_ZONE, FORMATS } from "./config";

type ExtractedValues = Record<string, unknown> | undefined;
type MessageOrParams = string | { message: string; values?: ExtractedValues; formats?: unknown };
type FallbackTranslate = (
  key: undefined,
  values: ExtractedValues,
  formats: unknown,
  fallback: string,
) => unknown;

export type ExtractedTranslator = ReturnType<typeof _useExtracted>;

const sourceArgs = (
  catalog: Catalog | undefined,
  messageOrParams: MessageOrParams,
  values?: ExtractedValues,
  formats?: unknown,
): Parameters<FallbackTranslate> => {
  const [message, callValues, callFormats] =
    typeof messageOrParams === "string"
      ? [messageOrParams, values, formats]
      : [messageOrParams.message, messageOrParams.values, messageOrParams.formats];
  return [undefined, callValues, callFormats, catalog?.get(message) ?? message];
};

/**
 * Wraps a use-intl translator (`useTranslations()` / `createTranslator()`) for source messages,
 * translated through `catalog` when one is given.
 */
export const toExtractedTranslator = (
  translator: unknown,
  catalog?: Catalog,
): ExtractedTranslator => {
  const t = translator as FallbackTranslate & {
    rich: FallbackTranslate;
    markup: FallbackTranslate;
  };
  type Args = [MessageOrParams, ExtractedValues?, unknown?];
  const translate = (...args: Args) => t(...sourceArgs(catalog, ...args));
  translate.rich = (...args: Args) => t.rich(...sourceArgs(catalog, ...args));
  translate.markup = (...args: Args) => t.markup(...sourceArgs(catalog, ...args));
  translate.has = () => true;
  return translate as unknown as ExtractedTranslator;
};

const translators = new Map<AppLocale, ExtractedTranslator>();

/**
 * The one translator per locale that every `useExtracted()` / `getExtracted()` call returns, in the
 * browser, during server rendering and in server components. A use-intl translator per call (what
 * `useTranslations()` creates for each component) starts with empty caches and, with no catalog,
 * constructs two `Error`s; on a large docs page that was about half of the server render time.
 * A non-English locale needs its loaded catalog; without one, the English translator answers.
 */
export const extractedTranslatorFor = (
  locale: AppLocale,
  catalog?: Catalog,
): ExtractedTranslator => {
  if (locale !== DEFAULT_LOCALE && !catalog) return extractedTranslatorFor(DEFAULT_LOCALE);
  let translator = translators.get(locale);
  if (!translator) {
    translator = toExtractedTranslator(
      createTranslator({ locale, formats: FORMATS, timeZone: DEFAULT_TIME_ZONE }),
      locale === DEFAULT_LOCALE ? undefined : catalog,
    );
    translators.set(locale, translator);
  }
  return translator;
};

/** The English translator: public pages, docs, server components and server functions. */
export const sharedExtractedTranslator = (): ExtractedTranslator =>
  extractedTranslatorFor(DEFAULT_LOCALE);

/** `useTranslateText()`: data text looked up in `catalog` by its English, else left as it is. */
export const translateTextWith =
  (catalog: Catalog | undefined) =>
  <Text extends string | null | undefined>(text: Text): Text | string =>
    (text && catalog?.get(text)) || text;
